import type { ReactNode } from 'react';
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router-dom';
import type { Permission } from '@technohull/shared';
import { AppShell } from './components/AppShell.js';
import { Placeholder, RequirePermission } from './pages/common.js';
import { Explore } from './pages/Explore.js';
import { Dashboard } from './pages/Dashboard.js';
import { Overview } from './pages/Overview.js';
import { Pdi } from './pages/Pdi.js';
import { VesselProgress } from './pages/VesselProgress.js';
import { Board } from './pages/Board.js';
import { AssemblyProgress } from './pages/AssemblyProgress.js';
import { AssemblyHub } from './pages/AssemblyHub.js';
import { ManagementHub } from './pages/ManagementHub.js';
import { AssemblyAlerts } from './pages/AssemblyAlerts.js';
import { EngineOrders } from './pages/EngineOrders.js';
import { WashingPlan } from './pages/WashingPlan.js';
import { Reporting } from './pages/Reporting.js';
import { Labels } from './pages/Labels.js';
import { Grp } from './pages/Grp.js';
import { Schedule } from './pages/Schedule.js';
import { Agenda } from './pages/Agenda.js';
import { SalesOrders } from './pages/SalesOrders.js';
import { AssemblyOrders } from './pages/AssemblyOrders.js';
import { Configurator } from './pages/Configurator.js';
import { CustomerDatabase } from './pages/CustomerDatabase.js';
import { CustomerDuplicates } from './pages/CustomerDuplicates.js';
import {
  ChangeLog,
  Gmail,
  OrderSheets,
} from './pages/ExternalLinks.js';
import { Calendar } from './pages/Calendar.js';
import { Loading } from './pages/Loading.js';
import { Deck } from './pages/Deck.js';
import { VesselIdentification } from './pages/VesselIdentification.js';
import { Service } from './pages/Service.js';
import { ServiceRequests } from './pages/ServiceRequests.js';
import { Documents } from './pages/Documents.js';
import { Ds } from './pages/Ds.js';
import { InfoFiles } from './pages/InfoFiles.js';
import { BomLists } from './pages/BomLists.js';
import { OrderFile } from './pages/OrderFile.js';
import { SerialNumbers } from './pages/SerialNumbers.js';
import { OrderSheet } from './pages/OrderSheet.js';
import { WarehouseRollup } from './pages/WarehouseRollup.js';
import { AssemblyWeights } from './pages/AssemblyWeights.js';
import { Overtime } from './pages/Overtime.js';
import { Employees } from './pages/Employees.js';
import { VesselStatus } from './pages/VesselStatus.js';
import { FuelTankSn } from './pages/FuelTankSn.js';
import { Fueling } from './pages/Fueling.js';
import { AdminUsers } from './pages/admin/Users.js';
import { AdminRoles } from './pages/admin/Roles.js';
import { AdminAudit } from './pages/admin/Audit.js';
import { AdminNotifications } from './pages/admin/Notifications.js';
import { AdminMappings } from './pages/admin/Mappings.js';
import { AdminAnnouncements } from './pages/admin/Announcements.js';
import { AdminDashboard } from './pages/admin/Dashboard.js';
import { AdminHub } from './pages/admin/AdminHub.js';
import { AdminCodingStats } from './pages/admin/CodingStats.js';
import { AdminTickets } from './pages/admin/Tickets.js';
import { ConfiguratorManagement } from './pages/admin/ConfiguratorManagement.js';
import { Settings } from './pages/Settings.js';

function guard(perm: Permission, el: ReactNode): ReactNode {
  return <RequirePermission perm={perm}>{el}</RequirePermission>;
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Explore /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'settings', element: <Settings /> },
      { path: 'schedule', element: guard('schedule:read', <Schedule />) },
      { path: 'agenda', element: guard('agenda:read', <Agenda />) },
      { path: 'assembly-hub', element: <AssemblyHub /> },
      { path: 'management-hub', element: guard('management:view', <ManagementHub />) },
      { path: 'assembly-alerts', element: guard('overview:read', <AssemblyAlerts />) },
      { path: 'engine-orders', element: guard('overview:read', <EngineOrders />) },
      { path: 'washing', element: <RequirePermission anyPerm={['overview:read', 'service:read']}><WashingPlan /></RequirePermission> },
      { path: 'overview', element: guard('overview:read', <Overview />) },
      { path: 'pdi', element: guard('pdi:read', <Pdi />) },
      { path: 'vessel-progress', element: <RequirePermission anyPerm={['overview:read', 'sales:read']}><VesselProgress /></RequirePermission> },
      { path: 'board', element: guard('board:read', <Board />) },
      { path: 'assembly', element: guard('production:read', <AssemblyProgress />) },
      { path: 'reporting', element: guard('report:read', <Reporting />) },
      { path: 'labels', element: guard('labels:read', <Labels />) },
      { path: 'grp', element: guard('grp:read', <Grp />) },
      { path: 'sales/orders', element: guard('sales:read', <SalesOrders />) },
      { path: 'assembly-orders', element: guard('prodorders:view', <AssemblyOrders />) },
      { path: 'configurator', element: guard('order:create', <Configurator />) },
      { path: 'sales/customers', element: guard('customer_database.view', <CustomerDatabase />) },
      { path: 'sales/customers/duplicates', element: guard('customer_database.view', <CustomerDuplicates />) },
      { path: 'sales/sheets', element: guard('sales:read', <OrderSheets />) },
      { path: 'sales/log', element: guard('sales:read', <ChangeLog />) },
      { path: 'ds', element: guard('ds:read', <Ds />) },
      { path: 'info-files', element: guard('production:read', <InfoFiles />) },
      { path: 'bom-lists', element: guard('production:read', <BomLists />) },
      { path: 'order-file/:sap', element: guard('production:read', <OrderFile />) },
      { path: 'serial-numbers/:sap', element: guard('production:read', <SerialNumbers />) },
      { path: 'order-sheet', element: guard('production:read', <OrderSheet />) },
      { path: 'assembly-weights', element: guard('production:read', <AssemblyWeights />) },
      { path: 'overtime', element: guard('production:read', <Overtime />) },
      { path: 'employees', element: guard('production:read', <Employees />) },
      { path: 'vessel-status', element: guard('vesselstatus:read', <VesselStatus />) },
      { path: 'fuel-tank-sn', element: guard('vesselstatus:read', <FuelTankSn />) },
      { path: 'order-rollup', element: guard('production:read', <WarehouseRollup />) },
      { path: 'accounting/fueling', element: guard('fueling.view', <Fueling />) },
      { path: 'service', element: guard('service:read', <Service />) },
      { path: 'service-requests', element: guard('servicereq:read', <ServiceRequests />) },
      { path: 'documents', element: guard('service:read', <Documents />) },
      { path: 'deck', element: guard('overview:read', <Deck />) },
      { path: 'loading', element: guard('overview:read', <Loading />) },
      { path: 'vessel-identification', element: guard('hin:read', <VesselIdentification />) },
      { path: 'calendar', element: <Calendar /> },
      { path: 'gmail', element: <Gmail /> },
      { path: 'admin', element: <AdminHub /> },
      { path: 'admin/tickets', element: guard('admin:tickets', <AdminTickets />) },
      { path: 'admin/users', element: guard('admin:users', <AdminUsers />) },
      { path: 'admin/roles', element: guard('admin:roles', <AdminRoles />) },
      { path: 'admin/audit', element: guard('admin:audit', <AdminAudit />) },
      { path: 'admin/coding-stats', element: guard('admin:audit', <AdminCodingStats />) },
      { path: 'admin/notifications', element: guard('admin:config', <AdminNotifications />) },
      { path: 'admin/announcements', element: guard('admin:config', <AdminAnnouncements />) },
      { path: 'admin/dashboard', element: guard('admin:config', <AdminDashboard />) },
      { path: 'admin/mappings', element: guard('admin:mappings', <AdminMappings />) },
      { path: 'sales/configurator', element: guard('configurator:manage', <ConfiguratorManagement />) },
      { path: '*', element: <Placeholder title="Not found" /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}
